import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    User,
    Phone,
    Users,
    DollarSign,
    CreditCard,
    Calendar,
    Clock,
    ArrowLeft,
    Plus,
    Trash2,
    CheckCircle2,
    AlertCircle,
    History
} from 'lucide-react';
import {
    ButtonPrimary,
    ButtonSecondary,
    Card,
    Badge,
    Modal,
    Input,
    Alert,
    Spinner,
    StatCard
} from '../../components/ComponentLibrary';
import {
    getStudentFinancialProfile,
    createPayment,
    deletePayment,
    getActivityLogs
} from '../../services/api';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


export default function StudentFinancialProfile() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [error, setError] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    // Confirm Modal & Alert state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    const [newPayment, setNewPayment] = useState({
        studentId: id,
        amount: '',
        paymentMethod: 'VodafoneCash',
        receivedBy: 'Abdullah',
        notes: '',
        installmentNumber: '',
        date: new Date().toISOString().split('T')[0]
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const result = await getStudentFinancialProfile(id);
            setData(result);
            fetchHistory();
            setError(null);
        } catch (err) {
            console.error('Fetch student profile error:', err);
            setError('Failed to load student financial profile.');
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            setLoadingHistory(true);
            const response = await getActivityLogs({ studentId: id });
            setHistory(response.data);
        } catch (err) {
            console.error('Fetch student history error:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [id]);

    const handleAddPayment = async (e) => {
        e.preventDefault();
        try {
            await createPayment(newPayment);
            setIsAddModalOpen(false);
            setNewPayment({
                ...newPayment,
                amount: '',
                notes: '',
                installmentNumber: '',
                date: new Date().toISOString().split('T')[0]
            });
            fetchData();
        } catch (err) {
            showAlert('Operation Failed', err.message || 'Failed to add payment', 'danger');
        }
    };


    const handleDeletePayment = (pid) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Payment?',
            message: 'Are you sure you want to delete this payment record? This will update the student balance and cannot be undone.',
            type: 'danger',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await deletePayment(pid);
                    fetchData();
                    showAlert('Success', 'Payment deleted successfully', 'info');
                } catch (err) {
                    showAlert('Deletion Failed', 'Failed to delete payment', 'danger');
                }
            }
        });
    };


    if (loading) return (
        <div className="admin-loading-state">
            <Spinner size="lg" />
            <p>Loading financial history...</p>
        </div>
    );

    if (error || !data) return <Alert variant="error">{error || 'Student not found'}</Alert>;

    const { student, payments, summary } = data;

    return (
        <div className="student-financial-profile">
            {/* Top Navigation */}
            <div className="profile-header-nav">
                <button className="back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} /> Back
                </button>
                <div className="header-actions">
                    <ButtonPrimary onClick={() => setIsAddModalOpen(true)}>
                        <Plus size={18} className="mr-2" /> Log Installment
                    </ButtonPrimary>
                </div>
            </div>

            {/* Main Profile Grid */}
            <div className="profile-main-grid">

                {/* Left Column: Summary and Info */}
                <div className="profile-sidebar">
                    <Card className="student-info-card" hoverable={false}>
                        <div className="profile-avatar-large">
                            {student.fullName?.[0] || '?'}
                        </div>
                        <h2 className="student-name-title">{student.fullName}</h2>

                        <div className="info-badge-group">
                            <Badge variant={
                                summary.paymentStatus === 'Paid' ? 'success' :
                                    summary.paymentStatus === 'Partial' ? 'warning' : 'error'
                            }>
                                {summary.paymentStatus}
                            </Badge>
                            <Badge variant="ocean">{student.track}</Badge>
                        </div>

                        <div className="contact-info-list">
                            <div className="info-item">
                                <Phone size={16} />
                                <span>{student.phone || 'No phone added'}</span>
                            </div>
                            <div className="info-item">
                                <Users size={16} />
                                <span>{student.groupName || 'No group assigned'}</span>
                            </div>
                        </div>

                        <div className="fees-summary-v2">
                            <div className="fee-row">
                                <span className="label">Total Required</span>
                                <span className="value">{student.totalFees} EGP</span>
                            </div>
                            <div className="fee-row highlight">
                                <span className="label">Total Paid</span>
                                <span className="value text-success">{summary.totalPaid} EGP</span>
                            </div>
                            <div className="fee-divider"></div>
                            <div className="fee-row large">
                                <span className="label">Remaining</span>
                                <span className="value text-danger">{summary.remainingAmount} EGP</span>
                            </div>
                        </div>
                    </Card>

                    <Card className="summary-stats-card" hoverable={false}>
                        <h3 className="card-sub-title"><CreditCard size={18} /> Collection Metrics</h3>
                        <div className="stat-mini-grid">
                            <div className="mini-stat">
                                <span className="mini-label">Installments</span>
                                <span className="mini-value">{summary.paymentCount}</span>
                            </div>
                            <div className="mini-stat">
                                <span className="mini-label">First Payment</span>
                                <span className="mini-value date">{summary.firstPaymentDate ? new Date(summary.firstPaymentDate).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            <div className="mini-stat">
                                <span className="mini-label">Last Payment</span>
                                <span className="mini-value date">{summary.lastPaymentDate ? new Date(summary.lastPaymentDate).toLocaleDateString() : 'N/A'}</span>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Right Column: Timeline */}
                <div className="profile-timeline-content">
                    <Card className="timeline-card" hoverable={false}>
                        <div className="card-header-flex">
                            <div className="card-title-group">
                                <History className="title-icon" size={24} />
                                <div>
                                    <h3>Payment Timeline</h3>
                                    <p>Historical record of received installments</p>
                                </div>
                            </div>
                        </div>

                        <div className="installment-timeline">
                            {payments.length > 0 ? payments.map((p, idx) => (
                                <div className="timeline-item" key={p._id}>
                                    <div className="timeline-marker">
                                        <div className="marker-dot"></div>
                                        {idx !== payments.length - 1 && <div className="marker-line"></div>}
                                    </div>
                                    <div className="timeline-box">
                                        <div className="box-header">
                                            <div className="installment-title">
                                                <span className="inst-num">
                                                    {p.installmentNumber ? `Installment #${p.installmentNumber}` : 'General Payment'}
                                                </span>
                                                <span className="inst-date">
                                                    <Clock size={12} /> {new Date(p.date).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="inst-amount">
                                                {p.amount} EGP
                                            </div>
                                        </div>
                                        <div className="box-details">
                                            <div className="detail-tag">
                                                <strong>Method:</strong> {p.paymentMethod}
                                            </div>
                                            <div className="detail-tag">
                                                <strong>Received By:</strong> {p.receivedBy}
                                            </div>
                                            {p.notes && (
                                                <div className="detail-notes">
                                                    <AlertCircle size={14} /> {p.notes}
                                                </div>
                                            )}
                                        </div>
                                        <button
                                            className="delete-payment-mini"
                                            onClick={() => handleDeletePayment(p._id)}
                                            title="Delete Payment Record"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            )) : (
                                <div className="empty-timeline">
                                    <DollarSign size={48} />
                                    <p>No payments recorded for this student yet.</p>
                                    <ButtonSecondary onClick={() => setIsAddModalOpen(true)} size="sm">
                                        Record First Payment
                                    </ButtonSecondary>
                                </div>
                            )}
                        </div>
                    </Card>

                    <Card className="student-history-card mt-4" hoverable={false}>
                        <div className="card-header-flex">
                            <div className="card-title-group">
                                <AlertCircle className="title-icon" size={24} style={{ color: 'var(--admin-accent)' }} />
                                <div>
                                    <h3>Administrative History</h3>
                                    <p>Audit trail of all system changes for this student</p>
                                </div>
                            </div>
                        </div>

                        <div className="mini-history-list">
                            {loadingHistory ? (
                                <div className="p-4 text-center"><Spinner size="sm" /></div>
                            ) : history.length > 0 ? history.map(log => (
                                <div className="history-item-mini" key={log._id}>
                                    <div className="hist-meta">
                                        <span className="hist-time">{new Date(log.createdAt).toLocaleString()}</span>
                                        <span className="hist-admin">by {log.performedByName}</span>
                                    </div>
                                    <div className="hist-desc">{log.description}</div>
                                </div>
                            )) : (
                                <div className="p-4 text-center text-muted">No administrative logs recorded.</div>
                            )}
                        </div>
                    </Card>
                </div>
            </div>

            {/* Add Payment Modal */}
            <Modal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                title={`💰 Log Payment: ${student.fullName}`}
                size="md"
            >
                <form onSubmit={handleAddPayment} className="finance-modal-form">
                    <div className="form-grid">
                        <Input
                            label="Amount (EGP) *"
                            type="number"
                            placeholder="0.00"
                            value={newPayment.amount}
                            onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                            required
                        />

                        <Input
                            label="Installment #"
                            type="number"
                            placeholder="e.g. 1"
                            value={newPayment.installmentNumber}
                            onChange={(e) => setNewPayment({ ...newPayment, installmentNumber: e.target.value })}
                        />

                        <div className="form-group">
                            <label className="cw-input-label">Payment Date</label>
                            <Input
                                type="date"
                                value={newPayment.date}
                                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                            />
                        </div>

                        <div className="form-group">
                            <label className="cw-input-label">Method *</label>
                            <select
                                className="cw-input"
                                value={newPayment.paymentMethod}
                                onChange={(e) => setNewPayment({ ...newPayment, paymentMethod: e.target.value })}
                                required
                            >
                                <option value="VodafoneCash">Vodafone Cash</option>
                                <option value="InstaPay">InstaPay</option>
                                <option value="Cash">Cash</option>
                            </select>
                        </div>

                        <div className="form-group full-width">
                            <label className="cw-input-label">Received By *</label>
                            <select
                                className="cw-input"
                                value={newPayment.receivedBy}
                                onChange={(e) => setNewPayment({ ...newPayment, receivedBy: e.target.value })}
                                required
                            >
                                <option value="Abdullah">Abdullah</option>
                                <option value="Mohamed">Mohamed</option>
                            </select>
                        </div>

                        <div className="full-width">
                            <label className="cw-input-label">Internal Notes</label>
                            <textarea
                                className="cw-input cw-textarea"
                                placeholder="..."
                                value={newPayment.notes}
                                onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                                rows="3"
                            />
                        </div>
                    </div>

                    <div className="modal-actions-footer">
                        <ButtonSecondary onClick={() => setIsAddModalOpen(false)} type="button">Cancel</ButtonSecondary>
                        <ButtonPrimary type="submit">Confirm & Add Payment</ButtonPrimary>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>

    );
}
